import { useEffect, useState } from 'react';
import { ErrorType } from '../content';
import { pollStatus } from './pollStatus';
import { triggerInit } from './triggerInit';

const isDev = process.env.NODE_ENV === 'development';
const POLLING_INTERVAL = 2_500;
const WAIT_FOR_REDIRECT = 500;

/**
 * Custom React hook to manage the polling and initialization flow for a job.
 *
 * - Initiates a creating a veeqo account by sending a POST request to the INIT_ENDPOINT.
 * - Polls the STATUS_ENDPOINT at a regular interval to check the job status.
 * - Redirects the user upon job completion.
 * - Handles and exposes error and ready states.
 *
 * @returns {{ ready: boolean, error: ErrorType | undefined }}
 *   An object containing the ready state and any error encountered.
 */
export const usePolling = (entryType: 'landing' | 'lwsc' | 'insc') => {
  let initEndpoint: string = '';
  let statusEndpoint: string = '';
  let redirectPath: string = '';

  if (entryType === 'lwsc') {
    initEndpoint = '/vq/sc/lwsc/init';
    statusEndpoint = '/vq/sc/lwsc/status';
    redirectPath = '/vq/sc/lwsc/redirect';
  }
  if (entryType === 'insc') {
    initEndpoint = '/vq/sc/insc/init';
    statusEndpoint = '/vq/sc/insc/status';
    redirectPath = '/vq/sc/insc/redirect';
  }
  if (entryType === 'landing') {
    initEndpoint = '/vq/sc/init';
    statusEndpoint = '/vq/sc/init/status';
    redirectPath = '/vq/sc/init/redirect';
  }

  const getRedirectPath = () => {
    const queryParams = window.location.search;
    if (queryParams.length === 0) return `${redirectPath}?fromSeCe=true`;
    if (!queryParams.includes('fromSeCe=true')) {
      return `${redirectPath}${queryParams}&fromSeCe=true`;
    }
    return `${redirectPath}${queryParams}`;
  };

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<ErrorType | undefined>(undefined);
  const [initiated, setInitiated] = useState(false);
  const [jobId, setJobId] = useState<string | undefined>(undefined);

  const setAsReady = () => {
    setReady(true);
    setTimeout(() => {
      const redirectPathVal = getRedirectPath();
      window.vqMetrics.logRedirect({ redirectPath: redirectPathVal, jobId });
      window.location.href = redirectPathVal;
    }, WAIT_FOR_REDIRECT);
  };
  if (isDev) {
    (window as any).setAsReady = setAsReady;
    (window as any).setError = setError;
  }

  useEffect(() => {
    if (!initiated) return;
    pollStatus({
      endpoint: statusEndpoint,
      setAsReady,
      setError,
      jobId,
      pollingInterval: POLLING_INTERVAL,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiated, jobId]);

  useEffect(() => {
    const queryParams = window.location.search;
    if (!ready) {
      const params = new URLSearchParams(queryParams);
      // Should only be error_code, but handle errorCode just in case
      const errorCode = params.get('error_code') || params.get('errorCode');
      if (errorCode) {
        window.vqMetrics.logErrorDisplayed({ errorCode });
        setError(errorCode as ErrorType);
        return;
      }
      triggerInit({
        setError,
        setJobId,
        setInitiated,
        endpoint: `${initEndpoint}${queryParams}`,
      });
    }
  }, [initEndpoint, ready]);

  return { ready, error, jobId };
};
